import {
  BookOpen,
  Building2,
  GraduationCap,
  Landmark,
  MonitorCog,
  ReceiptText,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { SectionWrapper } from '../components/SectionWrapper';

import styles from './StakeholderDetailSection.module.css';

interface Stakeholder {
  body: string[];
  icon: LucideIcon;
  summary: string;
  title: string;
}

const stakeholders: Stakeholder[] = [
  {
    body: [
      'Kudu gives academic leadership a practical way to reduce student costs while preserving faculty responsibility for the course.',
      'Faculty can start from a complete pre-built course, edit freely, or bring their own materials into a supported custom build.',
    ],
    icon: Landmark,
    summary:
      'Protect academic standing, lower student costs, and keep faculty responsible for the course — without a new hire.',
    title: 'Provost & VP TnL',
  },
  {
    body: [
      'Kudu lowers the support load by making the default course already usable, accessible, and AI-enabled.',
      'Training can focus on pedagogy and adoption instead of stitching together a publisher platform, homework tool, polling app, and AI workflow.',
    ],
    icon: GraduationCap,
    summary: 'Faculty support that scales without growing your team.',
    title: 'Teaching & Learning',
  },
  {
    body: [
      'The IT review gets simpler because the stack is consolidated: one vendor, one security posture, one AI position, one accessibility surface.',
      'Kudu is designed to give teams clear written answers on FERPA, data handling, AI guardrails, and support expectations.',
    ],
    icon: MonitorCog,
    summary: 'One vendor review replaces five. AI you can defend in writing.',
    title: 'CIO & IT',
  },
  {
    body: [
      'Kudu can fit the LMS and inclusive-access workflow with SSO, roster alignment, and grade passback paths.',
      'The institutional benefit is not another tool: it is a simpler courseware layer that can be adopted across large introductory courses.',
    ],
    icon: Building2,
    summary: 'This means your inclusive-access program can break even.',
    title: 'LMS',
  },
  {
    body: [
      'At $25 per student, the bookstore can offer a cleaner, more affordable product without forcing students into a fragmented set of purchases.',
      'Kudu is designed to sit inside inclusive-access economics instead of working around them.',
    ],
    icon: ReceiptText,
    summary: 'Built to fit into your inclusive-access program seamlessly.',
    title: 'Bookstore',
  },
  {
    body: [
      "OpenStax and OER solve access to source material. Kudu solves the course infrastructure around it: digital delivery, homework, accessibility, analytics, and AI support.",
      'That lets libraries advance affordability and open-content goals without leaving faculty to assemble the missing platform layer themselves.',
    ],
    icon: BookOpen,
    summary:
      "The OER problem isn't quality; it's the digital infrastructure students need it in.",
    title: 'Librarian',
  },
];

export function StakeholderDetailSection() {
  return (
    <SectionWrapper background="white" data-testid="institutions-section-stakeholders">
      <div className={styles.section}>
        <div className={styles.header}>
          <h2 className={styles.title}>What Kudu means for each seat at the table.</h2>
          <p className={styles.subhead}>
            A note specifically for each of the people typically involved in
            courseware and inclusive-access decisions.
          </p>
        </div>

        <div className={styles.list}>
          {stakeholders.map(({ body, icon: Icon, summary, title }) => (
            <article className={styles.role} key={title}>
              <div className={styles.roleHeading}>
                <span aria-hidden="true" className={styles.icon}>
                  <Icon size={22} strokeWidth={2.2} />
                </span>
                <div>
                  <h3>{title}</h3>
                  <p className={styles.summary}>{summary}</p>
                </div>
              </div>
              <div className={styles.roleBody}>
                {body.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </article>
          ))}
        </div>
      </div>
    </SectionWrapper>
  );
}
